import {
  Button,
  DetailBox,
  addToast,
  showDialogConfirm,
} from '@octano/global-ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Col, Row } from 'reactstrap';

import GoBackToListButton from '../../../../../components/buttons/GoBackToListButton';
import { PathsLayouts } from '../../../../../config/routes';
import { removeTemporaryUnblock } from '../../StudentList/api';
import TemporaryUnblockModal from '../../StudentList/components/TemporaryUnblockModal';
import { useStudentCurrentAccountLoader } from '../../components/StudentCurrentAccountLoader';
import { useDebtLoader } from './DebtLoader';
import { StudentFinancialStatusCode } from '../types';

const keyPrefix = 'financialPortal.currentAccount.tabs.currentAccount.header';

interface FinancialStatusVisuals {
  label: string;
  backgroundColor: string;
}

/**
 * Header component rendered above the filters on the student current-account view.
 *
 * Shows:
 * - GoBackToListButton on the left.
 * - DetailBox with student name + ID on the right.
 * - Financial status Badge chip next to the DetailBox.
 * - Descriptive paragraph below.
 */
const statusKeyPrefix = 'financialPortal.currentAccount.financialStatus';

export default function DebtHeader() {
  const { t } = useTranslation();
  const { data: studentData } = useStudentCurrentAccountLoader();
  const { data: debtData, refresh } = useDebtLoader();

  const studentId = studentData?.studentId;
  const status = debtData?.studentFinancialStatus?.status;

  const [modalOpen, setModalOpen] = useState(false);

  const handleDelete = useCallback(() => {
    if (studentId == null) {
      return;
    }
    showDialogConfirm({
      icon: { name: 'warning', color: 'warning' },
      title: t(`${statusKeyPrefix}.deleteTitle`),
      subtitle: t(`${statusKeyPrefix}.deleteSubtitle`),
      btnConfirm: {
        text: t(`${statusKeyPrefix}.confirm`),
        onConfirm: async () => {
          const result = await removeTemporaryUnblock(studentId);
          if (result.error) {
            addToast({
              icon: 'error',
              color: 'danger',
              text: t(`${statusKeyPrefix}.errorDeleting`),
            });
            return;
          }
          addToast({
            icon: 'success',
            color: 'success',
            text: t(`${statusKeyPrefix}.successDeleting`),
          });
          refresh();
        },
      },
      btnClose: { text: t(`${statusKeyPrefix}.cancel`) },
    });
  }, [studentId, t, refresh]);

  const statusVisuals = useMemo<FinancialStatusVisuals | null>(() => {
    if (!status) {
      return null;
    }
    const map: Record<StudentFinancialStatusCode, FinancialStatusVisuals> = {
      [StudentFinancialStatusCode.Active]: {
        label: t(`${keyPrefix}.financialStatus.active`),
        backgroundColor: '#CEF0DA',
      },
      [StudentFinancialStatusCode.FinancialBlock]: {
        label: t(`${keyPrefix}.financialStatus.financialBlock`),
        backgroundColor: '#F8C6C6',
      },
      [StudentFinancialStatusCode.TemporaryUnblock]: {
        label: t(`${keyPrefix}.financialStatus.temporaryUnblock`),
        backgroundColor: '#FFE6A1',
      },
    };
    return map[status] ?? null;
  }, [status, t]);

  return (
    <div className="pt-3">
      {/* Row 1: GoBack + DetailBox + status chip */}
      <Row className="align-items-center">
        <Col xs="auto">
          <GoBackToListButton path={PathsLayouts.currentAccount} />
        </Col>
        <Col xs={12} md={4} className="ml-auto">
          <div className="d-flex align-items-center" style={{ gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <DetailBox
                columns={[
                  {
                    title: t(`${keyPrefix}.studentLabel`),
                    body: studentData?.name ?? '',
                    size: { xs: 12, md: 8 },
                  },
                  {
                    title: t(`${keyPrefix}.idLabel`),
                    body: studentData?.rutOrPassport ?? '',
                    size: { xs: 12, md: 4 },
                  },
                ]}
              />
            </div>
          </div>
        </Col>
      </Row>

      {/* Row 2: descriptive text */}
      <Row className="mt-3">
        <Col xs={6}>
          <p className="mb-0">{t(`${keyPrefix}.description`)}</p>
        </Col>
        <Col xs={6}>
          <div
            className="d-flex justify-content-end align-items-center"
            style={{ gap: '8px' }}
          >
            {statusVisuals ? (
              <Badge
                className="fs-14 fw-500 py-2 px-3 text-dark"
                style={{
                  backgroundColor: statusVisuals.backgroundColor,
                  minWidth: '140px',
                  whiteSpace: 'nowrap',
                }}
                pill
              >
                {statusVisuals.label}
              </Badge>
            ) : null}
            {status === StudentFinancialStatusCode.FinancialBlock && (
              <Button
                text=""
                icon="edit"
                size="sm"
                outlined
                onClick={() => setModalOpen(true)}
              />
            )}
            {status === StudentFinancialStatusCode.TemporaryUnblock && (
              <Button
                text=""
                icon="trash"
                size="sm"
                outlined
                onClick={handleDelete}
              />
            )}
          </div>
        </Col>
      </Row>

      {studentId != null && (
        <TemporaryUnblockModal
          isOpen={modalOpen}
          studentId={studentId}
          onClose={() => setModalOpen(false)}
          onSuccess={() => refresh()}
        />
      )}
    </div>
  );
}
